/**
 * FlowProcessor -- interactive processing screen for Flow Panels.
 *
 * Ported from JSX (master:frontend/src/components/FlowProcessor.jsx)
 * to TypeScript with Shadcn UI patterns.
 *
 * Two-column layout: step tracker + chat (left) | IPASS preview (right).
 * Pipeline logic lives in useFlowPipeline; IPASS rendering via ipassParser.
 */

import { FC, useRef, useEffect, useCallback, useState } from 'react'
import { Send, ChevronRight, Mic, Loader2 } from 'lucide-react'
import { cn } from '../lib/utils'
import { useAudioRecorder } from '../hooks/useAudioRecorder'
import { useTranscription } from '../hooks/useTranscription'
import { useChat } from '../contexts/ChatContext'
import useFlowPipeline from '../hooks/useFlowPipeline'
import type { FlowWorkspaceResults } from '../hooks/useFlowPipeline'
import DOMPurify from 'dompurify'
import { renderIPASSHtml } from '../utils/ipassParser'
import type { IPASSSections, LOCData } from '../utils/ipassParser'
import { useAutoScroll } from '../hooks/useAutoScroll'

// ────────────────────────────────────────────────────────
// Props
// ────────────────────────────────────────────────────────

interface FlowProcessorProps {
  workspaceId: string | null
  onComplete: (results: FlowWorkspaceResults) => void
  onConvertToAdmission?: () => void
  panelName?: string
  className?: string
}

// ────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────

export const FlowProcessor: FC<FlowProcessorProps> = ({
  workspaceId,
  onComplete,
  onConvertToAdmission,
  panelName,
  className,
}) => {
  const {
    snapshot,
    ipassSections,
    loc,
    isComplete,
    isError,
    elapsed,
    thinkingMsg,
    STEP_LABELS,
    chatMessages,
    userInput,
    setUserInput,
    handleSendMessage,
    handleEnterWorkspace,
  } = useFlowPipeline(workspaceId)

  const { transcribeAudio } = useChat()
  const { startRecording, stopRecording, isRecording, audioBlob, clearAudioBlob } = useAudioRecorder()
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null)

  // Process transcribed audio → append to input, surface failures inline
  useTranscription({
    audioBlob,
    clearAudioBlob,
    transcribeAudio,
    onText: text => setUserInput((prev: string) => (prev ? prev + ' ' : '') + text),
    onError: setTranscriptionError,
  })

  // Clear any prior transcription error, then start a fresh recording
  const handleStartRecording = useCallback(() => {
    setTranscriptionError(null)
    startRecording()
  }, [startRecording])

  const [eventsCollapsed, setEventsCollapsed] = useState(false)
  const [leftWidth, setLeftWidth] = useState(() => {
    try { return parseFloat(localStorage.getItem('casey-flow-split') || '50') }
    catch (err) { console.warn('[FlowProcessor] Failed to restore split ratio from localStorage:', err); return 50 }
  })
  const { messagesEndRef: feedEndRef, scrollContainerRef: feedRef, handleScroll: handleFeedScroll } = useAutoScroll(
    [snapshot?.events?.length, chatMessages.length],
    100
  )
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const isDragging = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Persist split ratio
  useEffect(() => {
    try { localStorage.setItem('casey-flow-split', String(leftWidth)) }
    catch (err) { console.warn('[FlowProcessor] Failed to persist split ratio to localStorage:', err) }
  }, [leftWidth])

  // Drag handler for resizable split
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const pct = ((e.clientX - rect.left) / rect.width) * 100
      setLeftWidth(Math.min(75, Math.max(25, pct)))
    }
    const onMouseUp = () => {
      isDragging.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [])

  const onEnterWorkspace = useCallback(() => {
    handleEnterWorkspace(onComplete)
  }, [handleEnterWorkspace, onComplete])

  return (
    <div ref={containerRef} className={cn('flex-1 flex min-h-0 overflow-hidden bg-background', className)}>
      {/* Left: Step tracker + chat */}
      <div className="flex flex-col min-w-[300px]" style={{ width: `${leftWidth}%` }}>
        {/* Header */}
        <div className="px-4 py-3 border-b bg-muted/30">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <h2 className="text-sm font-bold text-primary">Flow Processing</h2>
                {panelName && (
                  <>
                    <span className="text-muted-foreground">|</span>
                    <span className="text-sm font-bold text-purple-400">{panelName}</span>
                  </>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground">
                {snapshot?.fileCount || 0} file{(snapshot?.fileCount || 0) !== 1 ? 's' : ''} --{' '}
                {elapsed}s elapsed
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isComplete && (
                <button
                  onClick={() => {
                    if (window.confirm('Convert this flow case to a full admission workup?')) {
                      onEnterWorkspace()
                      setTimeout(() => onConvertToAdmission?.(), 100)
                    }
                  }}
                  className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-lg transition-colors"
                >
                  Start Admission
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Step tracker */}
        <div className="px-4 py-3 border-b bg-muted/20">
          <div className="flex items-center gap-1">
            {snapshot?.steps.map((stepId, idx) => {
              const status = snapshot.stepStatus[stepId]
              const isCurrent = idx === snapshot.currentStep && status === 'running'

              return (
                <div key={stepId} className="flex items-center gap-1 flex-1">
                  <div className={cn(
                    'flex items-center gap-1.5 px-2 py-1 rounded-md flex-1 transition-all',
                    status === 'complete' ? 'bg-emerald-500/10' :
                    isCurrent ? 'bg-blue-500/10 ring-1 ring-blue-500/30' :
                    status === 'error' ? 'bg-destructive/10' :
                    'bg-muted'
                  )}>
                    <span className={cn(
                      'text-xs',
                      status === 'complete' ? 'text-emerald-400' :
                      isCurrent ? 'text-blue-400' :
                      status === 'error' ? 'text-destructive' :
                      'text-muted-foreground'
                    )}>
                      {status === 'complete' ? '\u2713' : isCurrent ? '\u25CF' : status === 'error' ? '\u2717' : '\u25CB'}
                    </span>
                    <span className={cn(
                      'text-[10px] font-medium truncate',
                      status === 'complete' ? 'text-emerald-400/80' :
                      isCurrent ? 'text-blue-400' :
                      status === 'error' ? 'text-destructive' :
                      'text-muted-foreground'
                    )}>
                      {STEP_LABELS[stepId]}
                    </span>
                  </div>
                  {idx < snapshot.steps.length - 1 && (
                    <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                  )}
                </div>
              )
            })}
          </div>
          {thinkingMsg && (
            <p className="text-[10px] text-blue-400/70 mt-1.5 italic animate-pulse">
              {thinkingMsg}
            </p>
          )}
        </div>

        {/* Activity feed + chat */}
        <div ref={feedRef} onScroll={handleFeedScroll} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {/* Pipeline events -- collapsible */}
          {snapshot?.events.filter(evt =>
            !['user_message', 'casey_response', 'response'].includes(evt.type)
          ).length ? (
            <div className="mb-2">
              <button
                onClick={() => setEventsCollapsed(prev => !prev)}
                className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground hover:text-foreground transition-colors mb-1.5"
              >
                <span className={cn('transition-transform', !eventsCollapsed && 'rotate-90')}>
                  {'\u25B6'}
                </span>
                Pipeline Events ({snapshot.events.filter(evt =>
                  !['user_message', 'casey_response', 'response'].includes(evt.type)
                ).length})
              </button>
              {!eventsCollapsed && (
                <div className="space-y-1 pl-3 border-l">
                  {snapshot.events
                    .filter(evt => !['user_message', 'casey_response', 'response'].includes(evt.type))
                    .map(evt => (
                      <div key={evt.id} className="flex items-center gap-2 text-xs">
                        <span className={cn(
                          'w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 text-[8px] font-bold',
                          evt.type === 'error' ? 'bg-destructive/20 text-destructive' :
                          evt.type === 'step_complete' ? 'bg-emerald-500/20 text-emerald-400' :
                          evt.type === 'tool_call' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-blue-500/20 text-blue-400'
                        )}>
                          {evt.type === 'tool_call' ? '\u2699' : evt.type === 'error' ? '!' : evt.type === 'step_complete' ? '\u2713' : '\u25CF'}
                        </span>
                        <span className={cn(evt.type === 'error' ? 'text-destructive/80' : 'text-muted-foreground')}>
                          {evt.description}
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          ) : null}

          {/* Chat messages */}
          {chatMessages.map((msg, i) => {
            const safeText = typeof msg.text === 'string' ? msg.text : String(msg.text ?? '')
            return (
              <div key={`chat-${i}`} className={cn('flex items-start gap-3', msg.role === 'user' ? 'flex-row-reverse' : '')}>
                {msg.role === 'user' ? (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[10px] font-bold text-white">MD</span>
                  </div>
                ) : (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[10px] font-bold text-white">C</span>
                  </div>
                )}

                <div className={cn('max-w-[80%] flex flex-col', msg.role === 'user' ? 'items-end' : 'items-start')}>
                  <div className={cn(
                    'rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground rounded-tr-sm'
                      : 'bg-card text-card-foreground border rounded-tl-sm'
                  )}>
                    <div className="whitespace-pre-wrap">{safeText}</div>
                  </div>
                  <span className={cn('text-[10px] text-muted-foreground/50 mt-1 px-1', msg.role === 'user' ? 'text-right' : 'text-left')}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            )
          })}

          {isError && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-xs text-destructive">
              {snapshot?.error || 'An error occurred during processing.'}
            </div>
          )}

          <div ref={feedEndRef} />
        </div>

        {/* Chat input */}
        <div className="px-4 py-3 border-t bg-muted/30">
          <div className="flex items-end gap-2 bg-background border rounded-2xl px-3 py-2 focus-within:border-primary/50 transition-colors">
            <button
              type="button"
              onClick={isRecording ? stopRecording : handleStartRecording}
              className={cn(
                'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors',
                isRecording
                  ? 'bg-destructive text-destructive-foreground animate-pulse'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              )}
              title={isRecording ? 'Stop recording' : 'Voice input'}
            >
              {isRecording ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Mic className="w-3.5 h-3.5" />
              )}
            </button>
            <textarea
              ref={textareaRef}
              value={userInput}
              onChange={e => {
                setUserInput(e.target.value)
                if (transcriptionError) setTranscriptionError(null)
                e.target.style.height = 'auto'
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSendMessage()
                }
              }}
              placeholder={isRecording ? 'Listening...' : 'Chat with Casey during processing...'}
              rows={1}
              className="flex-1 bg-transparent text-sm text-foreground placeholder-muted-foreground focus:outline-none resize-none min-h-[24px] max-h-[120px] leading-relaxed"
            />
            <button
              onClick={() => {
                handleSendMessage()
                if (textareaRef.current) textareaRef.current.style.height = 'auto'
              }}
              disabled={!userInput.trim()}
              className={cn(
                'w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-colors',
                userInput.trim()
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'bg-muted text-muted-foreground'
              )}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          {/* Transcription error */}
          {transcriptionError && (
            <p className="mt-1.5 px-1 text-xs text-destructive">{transcriptionError}</p>
          )}
        </div>
      </div>

      {/* Drag handle */}
      <div
        className="w-1 hover:w-1.5 bg-border hover:bg-primary/50 cursor-col-resize transition-all flex-shrink-0"
        onMouseDown={() => {
          isDragging.current = true
          document.body.style.cursor = 'col-resize'
          document.body.style.userSelect = 'none'
        }}
      />

      {/* Right: IPASS preview */}
      <div className="flex flex-col min-w-[280px] bg-muted/20" style={{ width: `${100 - leftWidth}%` }}>
        <IPASSPreview sections={ipassSections} loc={loc} />
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────
// IPASS Preview sub-component
// ────────────────────────────────────────────────────────

interface IPASSPreviewProps {
  sections: IPASSSections
  loc: LOCData | null
}

const IPASSPreview: FC<IPASSPreviewProps> = ({ sections, loc }) => {
  return (
    <>
      <div className="px-4 py-3 border-b">
        <h3 className="text-sm font-bold text-primary">IPASS Signout</h3>
        <p className="text-[10px] text-muted-foreground">
          {Object.keys(sections).length}/6 sections populated
        </p>
      </div>

      {loc && (
        <div className="px-4 py-2.5 border-b bg-muted/20">
          <div className="text-xs">
            <p className="font-semibold text-foreground mb-1">Level of Care</p>
            <p className="text-muted-foreground">
              <span className="font-medium">{loc.disposition}</span>
              {loc.unit && ` | ${loc.unit}`}
              {loc.telemetry === 'yes' && ' | Tele'}
            </p>
            {loc.rationale && (
              <p className="text-muted-foreground/70 mt-0.5 text-[11px]">{loc.rationale}</p>
            )}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-3">
        <div
          className="prose prose-sm max-w-none dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderIPASSHtml(sections)) }}
        />
      </div>
    </>
  )
}


